import React, { useState, useEffect } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ShieldAlert, DollarSign, Save, RotateCcw, Layers, Trash2, Plus } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', slate: '#5C6B7A', bg: '#F5F7FA' };

const OVERRIDE_FIELDS = [
  { key: 'material_quantum', label: 'Materiality ($)' },
  { key: 'authority_ceiling', label: 'Authority ($)' },
  { key: 'renewal_premium_change_pct', label: 'Renewal Δ%' },
  { key: 'renewal_material_si', label: 'Renewal SI ($)' },
];

const TRIGGERS = [
  { key: 'trigger_material_quantum', label: 'Material quantum', desc: 'Escalate when estimated quantum is at or above the materiality line below.' },
  { key: 'trigger_beyond_authority', label: 'Beyond delegated authority', desc: 'Escalate when exposure (quantum or reserve) exceeds the authority ceiling below.' },
  { key: 'trigger_coverage_dispute', label: 'Coverage / liability dispute', desc: 'Escalate when the coverage or liability position is unresolved or contested.' },
  { key: 'trigger_fraud_siu', label: 'Fraud / SIU signal', desc: 'Escalate on fraud indicators at triage or an active Special Investigations referral.' },
  { key: 'trigger_regulatory_deadline', label: 'Regulatory-deadline breach', desc: 'Flag GI-Code / AFCA claimant deadlines that pass while a claim is on exception hold.' },
];

export default function AriaEscalationSettingsPage() {
  const api = useApi();
  const { user } = useAuth();
  const canEdit = ['mga_admin', 'platform_owner'].includes(user?.role);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState({});
  const [addClass, setAddClass] = useState('');

  const load = () => api.get('/claims/diary/config').then(r => setCfg(r.data)).catch(err => console.error('API error:', err?.message));
  useEffect(() => { load(); api.get('/insurance-classes').then(r => setClasses(r.data || {})).catch(() => {}); /* eslint-disable-next-line */ }, []);

  if (!cfg) return <div className="p-8 text-sm" style={{ color: C.slate }} data-testid="aria-settings-loading">Loading ARIA settings…</div>;

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const overrides = cfg.class_overrides || {};
  const setOverride = (cls, field, v) => setCfg(c => ({
    ...c,
    class_overrides: { ...(c.class_overrides || {}), [cls]: { ...((c.class_overrides || {})[cls] || {}), [field]: v } },
  }));
  const removeOverride = (cls) => setCfg(c => {
    const next = { ...(c.class_overrides || {}) };
    delete next[cls];
    return { ...c, class_overrides: next };
  });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/claims/diary/config', {
        material_quantum: Number(cfg.material_quantum),
        authority_ceiling: Number(cfg.authority_ceiling),
        digest_top_n: Number(cfg.digest_top_n),
        renewal_premium_change_pct: Number(cfg.renewal_premium_change_pct),
        renewal_material_si: Number(cfg.renewal_material_si),
        trigger_material_quantum: cfg.trigger_material_quantum,
        trigger_beyond_authority: cfg.trigger_beyond_authority,
        trigger_coverage_dispute: cfg.trigger_coverage_dispute,
        trigger_fraud_siu: cfg.trigger_fraud_siu,
        trigger_regulatory_deadline: cfg.trigger_regulatory_deadline,
        class_overrides: cfg.class_overrides || {},
      });
      setCfg(data);
      toast.success('ARIA escalation rules saved — applied on the next sweep');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="aria-settings-page">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-sm flex items-center justify-center shrink-0" style={{ background: 'rgba(0,180,216,0.12)' }}>
          <ShieldAlert className="w-5 h-5" style={{ color: C.teal }} />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight" style={{ color: C.navy }}>ARIA Escalation Rules</h1>
          <p className="text-sm mt-0.5" style={{ color: C.slate }}>
            ARIA runs the claims diary autonomously. Tune here exactly which exceptions it puts in front of the C-suite — everything else it handles itself.
          </p>
        </div>
      </div>

      {/* Thresholds */}
      <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.15)' }}>
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: C.navy }}>Thresholds</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="text-xs font-medium" style={{ color: C.slate }}>Materiality line ($)</label>
            <div className="flex items-center gap-2 mt-1">
              <DollarSign className="w-4 h-4" style={{ color: C.slate }} />
              <Input type="number" value={cfg.material_quantum} disabled={!canEdit} data-testid="aria-material-quantum"
                onChange={e => set('material_quantum', e.target.value)} className="rounded-sm" />
            </div>
            <p className="text-[11px] mt-1" style={{ color: C.slate }}>Claims at or above this quantum go to the C-suite.</p>
          </div>
          <div>
            <label className="text-xs font-medium" style={{ color: C.slate }}>Delegated authority ceiling ($)</label>
            <div className="flex items-center gap-2 mt-1">
              <DollarSign className="w-4 h-4" style={{ color: C.slate }} />
              <Input type="number" value={cfg.authority_ceiling} disabled={!canEdit} data-testid="aria-authority-ceiling"
                onChange={e => set('authority_ceiling', e.target.value)} className="rounded-sm" />
            </div>
            <p className="text-[11px] mt-1" style={{ color: C.slate }}>Exposure above this needs Principal sign-off.</p>
          </div>
          <div>
            <label className="text-xs font-medium" style={{ color: C.slate }}>Digest — top N shown</label>
            <Input type="number" value={cfg.digest_top_n} disabled={!canEdit} data-testid="aria-digest-top-n"
              onChange={e => set('digest_top_n', e.target.value)} className="rounded-sm mt-1 max-w-[120px]" />
            <p className="text-[11px] mt-1" style={{ color: C.slate }}>How many exceptions to list in the C-suite email.</p>
          </div>
          <div>
            <label className="text-xs font-medium" style={{ color: C.slate }}>Renewal premium change (%)</label>
            <Input type="number" value={cfg.renewal_premium_change_pct} disabled={!canEdit} data-testid="aria-renewal-premium-pct"
              onChange={e => set('renewal_premium_change_pct', e.target.value)} className="rounded-sm mt-1 max-w-[120px]" />
            <p className="text-[11px] mt-1" style={{ color: C.slate }}>Renewals moving more than this escalate; the rest ARIA auto-offers.</p>
          </div>
          <div>
            <label className="text-xs font-medium" style={{ color: C.slate }}>Renewal material sum insured ($)</label>
            <div className="flex items-center gap-2 mt-1">
              <DollarSign className="w-4 h-4" style={{ color: C.slate }} />
              <Input type="number" value={cfg.renewal_material_si} disabled={!canEdit} data-testid="aria-renewal-material-si"
                onChange={e => set('renewal_material_si', e.target.value)} className="rounded-sm" />
            </div>
            <p className="text-[11px] mt-1" style={{ color: C.slate }}>Renewals at or above this SI always go to the C-suite.</p>
          </div>
        </div>
      </Card>

      {/* Triggers */}
      <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.15)' }}>
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.navy }}>Escalation Triggers</h3>
        <p className="text-[11px] mb-4" style={{ color: C.slate }}>Turn a trigger off and ARIA will handle those claims autonomously instead of escalating them.</p>
        <div className="space-y-1">
          {TRIGGERS.map(t => (
            <div key={t.key} className="flex items-center justify-between py-2.5 border-b last:border-0" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`aria-trigger-row-${t.key}`}>
              <div className="pr-4">
                <p className="text-sm font-medium" style={{ color: C.navy }}>{t.label}</p>
                <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>{t.desc}</p>
              </div>
              <Switch checked={!!cfg[t.key]} disabled={!canEdit} data-testid={`aria-toggle-${t.key}`}
                onCheckedChange={v => set(t.key, v)} />
            </div>
          ))}
        </div>
      </Card>

      {/* Per-class overrides */}
      <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="aria-class-overrides-card">
        <div className="flex items-center gap-2 mb-1">
          <Layers className="w-4 h-4" style={{ color: C.teal }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Per-Class Rules</h3>
        </div>
        <p className="text-[11px] mb-4" style={{ color: C.slate }}>Override thresholds for individual insurance classes — anything left blank inherits the global value above.</p>
        {Object.keys(overrides).length === 0 && (
          <p className="text-xs mb-3" style={{ color: C.slate }} data-testid="no-class-overrides">No per-class rules yet — all classes use the global thresholds.</p>
        )}
        <div className="space-y-3">
          {Object.entries(overrides).map(([cls, ov]) => (
            <div key={cls} className="flex flex-wrap items-end gap-3 pb-3 border-b last:border-0" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`class-override-row-${cls}`}>
              <span className="text-sm font-medium min-w-[160px]" style={{ color: C.navy }}>{classes[cls]?.name || cls}</span>
              {OVERRIDE_FIELDS.map(f => (
                <div key={f.key}>
                  <label className="text-[10px]" style={{ color: C.slate }}>{f.label}</label>
                  <Input type="number" value={ov[f.key] ?? ''} disabled={!canEdit} placeholder="global"
                    data-testid={`override-${cls}-${f.key}`}
                    onChange={e => setOverride(cls, f.key, e.target.value)} className="rounded-sm h-8 w-28 text-xs" />
                </div>
              ))}
              {canEdit && (
                <button onClick={() => removeOverride(cls)} className="p-1.5" title="Remove rule" data-testid={`remove-override-${cls}`}>
                  <Trash2 className="w-4 h-4" style={{ color: C.red }} />
                </button>
              )}
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 mt-4">
            <select value={addClass} onChange={e => setAddClass(e.target.value)} data-testid="add-class-select"
              className="h-9 rounded-sm border px-2 text-sm" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
              <option value="">Select a class…</option>
              {Object.entries(classes).filter(([id]) => !overrides[id]).map(([id, c]) => (
                <option key={id} value={id}>{c.name || id}</option>
              ))}
            </select>
            <Button size="sm" variant="outline" disabled={!addClass} data-testid="add-class-override-btn"
              onClick={() => { setOverride(addClass, 'material_quantum', ''); setAddClass(''); }} className="rounded-sm">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add class rule
            </Button>
          </div>
        )}
      </Card>

      {canEdit && (
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving} data-testid="aria-settings-save" className="rounded-sm text-white" style={{ background: C.navy }}>
            <Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : 'Save Rules'}
          </Button>
          <button onClick={load} className="text-xs flex items-center gap-1" style={{ color: C.slate }} data-testid="aria-settings-reset">
            <RotateCcw className="w-3.5 h-3.5" />Discard changes
          </button>
          {cfg.updated_by && <span className="text-[11px] ml-auto" style={{ color: C.slate }}>Last saved by {cfg.updated_by}{cfg.updated_at ? ` · ${new Date(cfg.updated_at).toLocaleString('en-AU')}` : ''}</span>}
        </div>
      )}
    </div>
  );
}
